import { CallExpressionShape, PropertyAccessShape, type ObjectShape, type Shape } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import { ACTIVITY_API_NAME, LANE_API_NAME } from '../constants/playbook-plugin-constants'
import { isRunAfterCallee } from '../utils/start-rule-resolver'

/**
 * Resolve startsAfterLanes: either from Run.After() args
 * or from an explicit startsAfterLanes array. Returns a comma-separated
 * GUID string or undefined.
 */
export function resolveStartsAfterLanes(
    config: ObjectShape,
    laneVariables: Map<string, CallExpressionShape>,
    nowIdKeyToGuid: Map<string, string>
): string | undefined {
    const startRuleShape = config.get('startRule')

    if (
        startRuleShape.is(CallExpressionShape) &&
        isRunAfterCallee(startRuleShape.as(CallExpressionShape).getCallee())
    ) {
        return resolveRunAfterArgs(startRuleShape.as(CallExpressionShape), laneVariables, nowIdKeyToGuid)
    }

    return resolveExplicitStartsAfter(config, 'startsAfterLanes', nowIdKeyToGuid)
}

/**
 * Resolve startsAfterActivities: either from Run.After() args
 * or from an explicit startsAfterActivities array. Returns a comma-separated
 * GUID string or undefined.
 */
export function resolveStartsAfterActivities(
    config: ObjectShape,
    activityVariables: Map<string, CallExpressionShape>,
    nowIdKeyToGuid: Map<string, string>
): string | undefined {
    const startRuleShape = config.get('startRule')

    if (
        startRuleShape.is(CallExpressionShape) &&
        isRunAfterCallee(startRuleShape.as(CallExpressionShape).getCallee())
    ) {
        return resolveRunAfterActivityArgs(startRuleShape.as(CallExpressionShape), activityVariables, nowIdKeyToGuid)
    }

    return resolveExplicitStartsAfter(config, 'startsAfterActivities', nowIdKeyToGuid)
}

/**
 * Resolve branch references from Run.After() args.
 *
 * When a downstream activity uses Run.After(decision.branches.critical),
 * the arg is a PropertyAccessShape with elements [varName, 'branches', branchId].
 * This function resolves such references to the branch's generated GUID.
 */
export function resolveStartsAfterBranches(
    config: ObjectShape,
    activityVariables: Map<string, CallExpressionShape>,
    nowIdKeyToGuid: Map<string, string>,
    branchIdMap: Map<string, Map<string, string>>
): string | undefined {
    const startRuleShape = config.get('startRule')

    if (
        !startRuleShape.is(CallExpressionShape) ||
        !isRunAfterCallee(startRuleShape.as(CallExpressionShape).getCallee())
    ) {
        return undefined
    }

    const runAfterCall = startRuleShape.as(CallExpressionShape)
    // Use getArguments(false) to preserve PropertyAccessShape instances
    const runAfterArgs = runAfterCall.getArguments(false)

    const branchGuids: string[] = []
    for (const arg of runAfterArgs) {
        if (!arg.is(PropertyAccessShape)) {
            continue
        }
        const propAccess = arg.as(PropertyAccessShape)
        const elements = propAccess.getElements()

        // Expect pattern: <varName>.branches.<branchId>
        if (elements.length !== 3 || elements[1].getName() !== 'branches') {
            continue
        }

        const varName = propAccess.getElement(0).getName()
        const branchId = propAccess.getElement(2).getName()

        const activityCallExpr = activityVariables.get(varName)
        if (!activityCallExpr) {
            continue
        }
        const idShape = extractIdFromCallExpression(activityCallExpr)
        if (!(idShape instanceof NowIdShape)) {
            continue
        }
        const activitySysId = nowIdKeyToGuid.get(String(idShape.getValue()))
        if (!activitySysId) {
            continue
        }

        const branchMap = branchIdMap.get(activitySysId)
        if (!branchMap) {
            continue
        }
        const branchGuid = branchMap.get(branchId)
        if (branchGuid) {
            // Platform format: <decisionActivitySysId>.<branchId>
            branchGuids.push(`${activitySysId}.${branchGuid}`)
        }
    }

    return branchGuids.length > 0 ? branchGuids.join(',') : undefined
}

function resolveRunAfterArgs(
    runAfterCall: CallExpressionShape,
    variables: Map<string, CallExpressionShape>,
    nowIdKeyToGuid: Map<string, string>
): string | undefined {
    const runAfterArgs = runAfterCall.getArguments()
    const guids = runAfterArgs.flatMap((arg) => {
        // getArguments() resolves IdentifierShape args to their bound values,
        // so Run.After(lane_0) args arrive here as resolved CallExpressionShapes
        // (the Lane(...) call that lane_0 was assigned to), not as identifiers.
        let callExpr: CallExpressionShape | undefined
        if (arg.is(CallExpressionShape)) {
            callExpr = arg.as(CallExpressionShape)
        } else if (arg.isIdentifier()) {
            const name = arg.asIdentifier().getName()
            callExpr = variables.get(name)
        }
        if (callExpr) {
            const idShape = extractIdFromCallExpression(callExpr)
            if (idShape instanceof NowIdShape) {
                const guid = nowIdKeyToGuid.get(String(idShape.getValue()))
                return guid ? [guid] : []
            }
        }
        // Branch references (e.g. decision.branches.xxx) are skipped here —
        // they are handled by resolveStartsAfterBranches.
        return []
    })
    return guids.length > 0 ? guids.join(',') : undefined
}

/**
 * Resolve activity GUIDs from Run.After() args, including Decision activity
 * GUIDs extracted from branch references (PropertyAccessShape).
 *
 * For Run.After(decision.branches.critical, otherActivity), this returns
 * both the Decision activity sys_id AND otherActivity sys_id.
 */
function resolveRunAfterActivityArgs(
    runAfterCall: CallExpressionShape,
    variables: Map<string, CallExpressionShape>,
    nowIdKeyToGuid: Map<string, string>
): string | undefined {
    const resolvedArgs = runAfterCall.getArguments()
    const guids: string[] = []

    for (const arg of resolvedArgs) {
        let callExpr: CallExpressionShape | undefined
        if (arg.is(CallExpressionShape)) {
            callExpr = arg.as(CallExpressionShape)
        } else if (arg.isIdentifier()) {
            const name = arg.asIdentifier().getName()
            callExpr = variables.get(name)
        }
        if (callExpr) {
            const idShape = extractIdFromCallExpression(callExpr)
            if (idShape instanceof NowIdShape) {
                const guid = nowIdKeyToGuid.get(String(idShape.getValue()))
                if (guid) {
                    guids.push(guid)
                }
            }
        }
    }

    // Also extract Decision activity sys_ids from branch references (unresolved args)
    const unresolvedArgs = runAfterCall.getArguments(false)
    for (const arg of unresolvedArgs) {
        if (!arg.is(PropertyAccessShape)) {
            continue
        }
        const propAccess = arg.as(PropertyAccessShape)
        const elements = propAccess.getElements()
        if (elements.length !== 3 || elements[1].getName() !== 'branches') {
            continue
        }
        const varName = propAccess.getElement(0).getName()
        const activityCallExpr = variables.get(varName)
        if (!activityCallExpr) {
            continue
        }
        const idShape = extractIdFromCallExpression(activityCallExpr)
        if (idShape instanceof NowIdShape) {
            const guid = nowIdKeyToGuid.get(String(idShape.getValue()))
            if (guid && !guids.includes(guid)) {
                guids.push(guid)
            }
        }
    }

    return guids.length > 0 ? guids.join(',') : undefined
}

/**
 * Extract $id from a Lane or Activity call expression.
 *
 * Lane:     Lane({ config: { $id } })                  — $id is in arg 0
 * Activity: Activity(activityDef, { config: { $id } })  — $id is in arg 1
 */
export function extractIdFromCallExpression(callExpr: CallExpressionShape): Shape | undefined {
    const callee = callExpr.getCallee()

    let argObj: ObjectShape | undefined
    if (callee === LANE_API_NAME || callee === 'wfa.playbook.lane') {
        argObj = callExpr.getArgument(0).ifObject()
    } else if (callee === ACTIVITY_API_NAME || callee === 'wfa.playbook.activity') {
        argObj = callExpr.getArgument(1).ifObject()
    }

    if (!argObj) {
        return undefined
    }

    const configObj = argObj.get('config')?.isUndefined() === false ? argObj.get('config').ifObject() : argObj
    return configObj?.get('$id')
}

function resolveExplicitStartsAfter(
    config: ObjectShape,
    fieldName: string,
    nowIdKeyToGuid: Map<string, string>
): string | undefined {
    const shape = config.get(fieldName)
    if (shape.isUndefined()) {
        return undefined
    }
    const arr = shape.ifArray()
    if (!arr) {
        return undefined
    }
    const guids = arr
        .getElements()
        .map((el) => {
            if (el instanceof NowIdShape) {
                return nowIdKeyToGuid.get(String(el.getValue()))
            }
            return el.ifString()?.getValue()
        })
        .filter((g): g is string => !!g)
    return guids.length > 0 ? guids.join(',') : undefined
}
